import {useMemo, type FC, type HTMLAttributes} from 'react'
import {clsx} from 'clsx'
import {Box, merge} from '@primer/react'
import {useSortable} from '@dnd-kit/sortable'
import {SortableListTrigger} from './SortableListTrigger'
import {useDragAndDrop} from '../hooks/use-drag-and-drop'
import type {BetterSystemStyleObject} from '@primer/react/lib-esm/sx'
import {usePrefersReducedMotion} from '@github-ui/use-prefers-reduced-motion'
import {SortableItemContext} from '../context/SortableItemContext'
import {testIdProps} from '@github-ui/test-id-props'

export interface SortableListItemProps extends Omit<HTMLAttributes<HTMLLIElement>, 'id'> {
  id: string | number
  index: number
  title: string
  containerSx?: BetterSystemStyleObject
  UNSAFE_hideSortableItemTrigger?: boolean
}

export const SortableListItem: FC<SortableListItemProps> = ({
  children,
  id,
  index,
  title,
  containerSx,
  style,
  UNSAFE_hideSortableItemTrigger,
  ...props
}) => {
  const prefersReducedMotion = usePrefersReducedMotion()
  const {setNodeRef, isDragging, transform, transition} = useSortable({
    id,
    data: {metadata: {id}},
  })

  const {isDropTarget, direction} = useDragAndDrop()

  const isOver = isDropTarget(id)

  const containerClassName = clsx(props.className, {
    ['color-bg-subtle']: isOver && isDragging,
    ['show-sash-over']: isOver,
  })

  const containerSashStyles: BetterSystemStyleObject = useMemo(
    () => ({
      position: 'relative',
      '&::after,  &::before': {
        ...(direction === 'horizontal' ? {top: 0, bottom: 0, width: '2px'} : {left: 0, right: 0, height: '2px'}),
        content: '""',
        display: 'none',
        position: 'absolute',
        bg: 'accent.emphasis',
        border: 0,
      },

      'body.is-dragging &.show-sash-over': {
        '&:before': {
          display: 'block',
          ...(direction === 'horizontal'
            ? {left: 0, transform: 'translateX(-50%)'}
            : {top: 0, transform: 'translateY(-50%)'}),
        },
        '&:after': {
          display: 'block',
          ...(direction === 'horizontal'
            ? {right: 0, transform: 'translateX(50%)'}
            : {bottom: 0, transform: 'translateY(50%)'}),
        },
      },
    }),
    [direction],
  )

  const containerStyles: BetterSystemStyleObject = containerSx
    ? merge<BetterSystemStyleObject>(containerSashStyles, containerSx)
    : containerSashStyles

  const draggingSx: BetterSystemStyleObject = isDragging
    ? {
        zIndex: 1,
        border: '1px solid',
        borderColor: 'border.muted',
        borderRadius: 2,
        backgroundColor: 'canvas.default',
        boxShadow: 'shadow.large',
      }
    : {}

  const sx: BetterSystemStyleObject = merge<BetterSystemStyleObject>(
    {
      display: 'relative',
      transform: transform
        ? `
        translateX(${Math.round(transform.x + (isDragging ? 8 : 0))}px)
        translateY(${Math.round(transform.y - (isDragging ? 8 : 0))}px)
        scaleX(${transform.scaleX})
        scaleY(${transform.scaleY})
      `
        : 'none',
      transition: prefersReducedMotion ? 'none' : transition,
      overflow: 'hidden',
    },
    draggingSx,
  )

  return (
    <SortableItemContext.Provider value={useMemo(() => ({index, title, id}), [index, id, title])}>
      <Box
        as="li"
        {...props}
        {...testIdProps('sortable-item')}
        className={containerClassName}
        sx={containerStyles}
        ref={setNodeRef}
      >
        <Box style={style} sx={sx}>
          {!UNSAFE_hideSortableItemTrigger && <SortableListTrigger />}
          {children}
        </Box>
      </Box>
    </SortableItemContext.Provider>
  )
}

try{ SortableListItem.displayName ||= 'SortableListItem' } catch {}